import React from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { ChatAiAssistHistoryEntry } from './ChatAiAssistHistoryEntry';
import { ChatAiAssistState } from '../../hooks/admin/useChatAiAssist';
import { ChatLinkTarget } from '../../utils/chatLinkCorpus';

interface ChatAiAssistPanelContentProps {
  assist: ChatAiAssistState;
  conversationId: string | null;
  corpus: ChatLinkTarget[] | null;
  scrollRef: React.RefObject<HTMLDivElement | null>;
  latestEntryRef: React.RefObject<HTMLDivElement | null>;
  onUseInvestorResponse?: (text: string) => void;
}

export const ChatAiAssistPanelContent: React.FC<ChatAiAssistPanelContentProps> = ({
  assist,
  conversationId,
  corpus,
  scrollRef,
  latestEntryRef,
  onUseInvestorResponse,
}) => (
  <Box ref={scrollRef} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 1.5, py: 1.25 }}>
    {assist.isStale && (
      <Box
        sx={{
          mb: 1,
          p: 1,
          borderRadius: 1,
          bgcolor: '#fff8e1',
          border: '1px solid #ffe082',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          The investor sent a newer message. Refresh to generate updated Vera assist.
        </Typography>
      </Box>
    )}

    {assist.loading && (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <CircularProgress size={18} />
        <Typography variant="body2" color="text.secondary">
          Preparing Vera assist…
        </Typography>
      </Box>
    )}

    {!assist.loading && assist.error && (
      <Typography variant="body2" color="error.main" sx={{ mb: 1 }}>
        {assist.error}
      </Typography>
    )}

    {!assist.loading && assist.history.length === 0 && !assist.error && (
      <Typography variant="body2" color="text.secondary">
        No Vera assist history yet for this conversation.
      </Typography>
    )}

    {assist.history.map((entry, index) => (
      <ChatAiAssistHistoryEntry
        key={entry.targetMessageId}
        entry={entry}
        expanded={assist.expandedTargetId === entry.targetMessageId}
        isLastEntry={index === assist.history.length - 1}
        latestEntryRef={index === assist.history.length - 1 ? latestEntryRef : undefined}
        assist={assist}
        conversationId={conversationId}
        corpus={corpus}
        onUseInvestorResponse={onUseInvestorResponse}
      />
    ))}
  </Box>
);
